// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import closeQuickViewModal from "@bg/App/Handlers/ProductQuickView/CloseQuickViewModal";
import { CustomAppState } from "@bg/App/Store/Reducers";
import LazyImage from "@bg/MobiusOverrides/LazyImage";
import { getCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getWishList } from "@insite/client-framework/Services/WishListService";
import siteMessage from "@insite/client-framework/SiteMessage";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import updateGetWishListsParameter from "@insite/client-framework/Store/Components/AddToListModal/Handlers/UpdateGetWishListsParameter";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import addToWishList, {
    AddToWishListParameter,
} from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import loadWishLists from "@insite/client-framework/Store/Data/WishLists/Handlers/LoadWishLists";
import { getWishListsDataView } from "@insite/client-framework/Store/Data/WishLists/WishListsSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { WishListModel } from "@insite/client-framework/Types/ApiModels";
import ScheduleReminderModal, {
    ScheduleReminderModalStyles,
} from "@insite/content-library/Components/ScheduleReminderModal";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import DynamicDropdown, { DynamicDropdownPresentationProps, OptionObject } from "@insite/mobius/DynamicDropdown";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import debounce from "lodash/debounce";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    extendedStyles?: AddToListModalStyles;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

const mapStateToProps = (state: CustomAppState) => ({
    session: state.context.session,
    modalIsOpen: state.components.addToListModal.isOpen,
    productInfos: state.components.addToListModal.productInfos,
    getWishListsParameter: state.components.addToListModal.getWishListsParameter,
    wishListsDataView: getWishListsDataView(state, state.components.addToListModal.getWishListsParameter),
    signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
    location: getLocation(state),
    projectListsPageUrl: getPageLinkByPageType(state, "MyListsPage")?.url,
    uncategorizedGroupName: (getSettingsCollection(state).customSettings as any).unCategorizedGroupName as string,
});

const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addToWishList,
    updateGetWishListsParameter,
    loadWishLists,
    closeQuickViewModal,
};

export interface GroupList {
    additionalInfo: string;
    description: string;
    id: string;
    name: string;
}

export interface AddToListModalStyles {
    modal?: ModalPresentationProps;
    copyWrapper?: InjectableCss;
    stepWrapper?: InjectableCss;
    groupsWrapper?: InjectableCss;
    groupItem?: ClickablePresentationProps;
    addNewGroupItem?: InjectableCss;
    addNewButton?: ButtonPresentationProps;
    manageListsLink?: LinkPresentationProps;
    innerWrapper?: InjectableCss;
    signInMessageText?: TypographyProps;
    descriptionText?: TypographyProps;
    listsSelect?: DynamicDropdownPresentationProps;
    newListInput?: TextFieldProps;
    buttonsWrapper?: InjectableCss;
    cancelButton?: ButtonPresentationProps;
    addButton?: ButtonPresentationProps;
    scheduleReminderCheckbox?: CheckboxPresentationProps;
    scheduleReminderModal?: ScheduleReminderModalStyles;
}

export const addToListModalStyles: AddToListModalStyles = {
    modal: {
        cssOverrides: {
            modalBody: css`
                ${getProp("theme.modal.defaultProps.cssOverrides.modalBody")};
                overflow: unset;
            `,
            modalContent: css`
                ${getProp("theme.modal.defaultProps.cssOverrides.modalContent")};
                overflow-y: visible;
            `,
        },
    },
    copyWrapper: {
        css: css`
            padding-bottom: 20px;
            margin-bottom: 25px;
            border-bottom: 1px solid ${getColor("secondary")};
        `,
    },
    stepWrapper: {
        // removed overflow and other styling stuff to fix dropdown overflow issue
        // if we keep the old styles, the body will have a horizontal scroll
        css: css`
            display: none;

            &&.in-view {
                display: block;
            }
        `,
    },
    descriptionText: {
        as: "p",
        size: "15px",
        lineHeight: "22px",
    },
    groupsWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            margin-right: -30px;
            @media (max-width: 760px) {
                margin-right: -10px;
            }
        `,
    },
    groupItem: {
        css: css`
            width: calc(100% / 4 - 30px);
            margin-right: 30px;
            text-align: center;
            display: block;
            margin-top: 35px;
            .item-wrapper {
                border: 1px solid transparent;
                display: flex;
                flex-direction: column;
                align-items: center;
            }
            &&.selected .item-wrapper {
                border-color: ${getColor("primary")};
            }
            @media (max-width: 760px) {
                width: calc(100% / 2 - 10px);
                margin-right: 10px;
            }
        `,
    },
    addNewGroupItem: {
        css: css`
            width: 100%;
            height: 162px;
            border: 2px dashed ${getColor("common.ui3")};
            border-radius: 4px;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-bottom: 15px;
        `,
    },
    addNewButton: {
        css: css`
            padding: 0;
            width: 40px;
            height: 40px;
            display: flex;
            align-items: center;
            justify-content: center;
            &&:hover {
                svg polygon {
                    fill: ${getColor("common.white")};
                }
            }
        `,
    },
    manageListsLink: {
        css: css`
            flex-grow: 1;
            justify-content: flex-end;
        `,
    },
    listsSelect: {
        cssOverrides: {
            formField: css`
                margin-bottom: 30px;
                label {
                    font-weight: 500;
                }
            `,
        },
    },
    newListInput: {
        cssOverrides: {
            formField: css`
                max-width: 540px;
                label {
                    font-weight: 500;
                }
            `,
        },
    },
    scheduleReminderCheckbox: {
        css: css`
            margin-top: 15px;
            align-items: flex-start;
        `,
    },
    buttonsWrapper: {
        css: css`
            margin-top: 30px;
            text-align: right;
        `,
    },
    cancelButton: {
        variant: "secondary",
    },
    addButton: {
        css: css`
            margin-right: 10px;
        `,
    },
};

export const LastUpdatedListIdCookieName = "LastUpdatedListId";

const AddToListModal = ({
    productInfos,
    session,
    modalIsOpen,
    getWishListsParameter,
    wishListsDataView,
    signInUrl,
    location,
    setAddToListModalIsOpen,
    addToWishList,
    updateGetWishListsParameter,
    loadWishLists,
    extendedStyles,
    projectListsPageUrl,
    history,
    uncategorizedGroupName,
    closeQuickViewModal,
}: Props) => {
    const [styles] = useState(() => mergeToNew(addToListModalStyles, extendedStyles));

    const toasterContext = useContext(ToasterContext);

    const isAuthenticated = session && (session.isAuthenticated || session.rememberMe) && !session.isGuest;
    const [selectedWishListId, setSelectedWishListId] = useState("new");
    const [newListName, setNewListName] = useState("");
    const [selectListError, setSelectListError] = useState<React.ReactNode>("");
    const [newListNameError, setNewListNameError] = useState<React.ReactNode>("");
    const [options, setOptions] = useState<OptionObject[]>([]);
    const [inProgress, setInProgress] = useState(false);
    const [isScheduled, setIsScheduled] = useState(false);
    const [scheduleReminderModalIsOpen, setScheduleReminderModalIsOpen] = useState(false);
    const [currentWishList, setCurrentWishList] = useState<WishListModel>();
    const [currentStep, setCurrentStep] = useState("");

    useEffect(() => {
        if (!modalIsOpen) {
            return;
        }

        const lastUpdatedListId = getCookie(LastUpdatedListIdCookieName) || "new";
        setSelectedWishListId(lastUpdatedListId);
    }, [modalIsOpen]);

    useEffect(() => {
        setNewListNameError("");
    }, [currentStep]);

    useEffect(() => {
        if (modalIsOpen && !wishListsDataView.value && !wishListsDataView.isLoading && isAuthenticated) {
            loadWishLists(getWishListsParameter);
        }
    }, [getWishListsParameter, modalIsOpen]);

    useEffect(() => {
        if (wishListsDataView.value) {
            let options: OptionObject[] = [];
            options = options.concat(
                wishListsDataView.value
                    .filter(o => !o.isAutogenerated)
                    .map(o => ({ optionText: o.name, optionValue: o.id })),
                );
            // Xng-Change push Create a New List option
            options.push({ optionText: translate("Create a New List"), optionValue: "new" });
            setOptions(options);
        }
    }, [wishListsDataView]);

    if (!productInfos) {
        return null;
    }

    const modalCloseHandler = () => {
        setNewListName("");
        setSelectListError("");
        setNewListNameError("");
        setCurrentStep("");
        setInProgress(false);
        setAddToListModalIsOpen({ modalIsOpen: false });
    };

    const listChangeHandler = (value?: string) => {
        setSelectedWishListId(value || "new");
        setSelectListError("");
    };

    const debouncedUpdateParameter = debounce((value: string) => {
        updateGetWishListsParameter({
            ...getWishListsParameter,
            query: value || undefined,
        });
    }, 200);

    const listsInputChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        debouncedUpdateParameter(event.currentTarget.value);
    };

    const newListInputChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setNewListName(event.target.value);
        if (event.target.value) {
            setNewListNameError("");
        }
    };

    const scheduleReminderChangeHandler = (_: React.SyntheticEvent<Element, Event>, value: boolean) => {
        setIsScheduled(!isScheduled);
    };

    const closeScheduleReminderModalHandler = () => {
        setScheduleReminderModalIsOpen(false);
        setIsScheduled(false);
        modalCloseHandler();
    };

    /**
     *
     * @param e : default click event, this can be undefined when creating a group from new group screen
     * @param newGroup : the new selected group from the custom group name screen
     */
    const addToListButtonClickHandler = (e: any, newGroup?: string) => {
        e && e.preventDefault();
        setNewListNameError("");
        setSelectListError("");
        
        const isNewList = selectedWishListId === "new";

        if ((isNewList && !newListName) || !selectedWishListId || !options.some(o => o.optionValue === selectedWishListId)
        ) {
            setSelectListError(siteMessage("Lists_Enter_New_Wishlist_Name"));
            return;
        }

        setInProgress(true);
        const newProductInfo = [] as any;
        productInfos.forEach((o, index) => {
            const newInfo = {
                ...o,
                properties: {
                    isUpdateGroup: true,
                    grouping: JSON.stringify({
                        wishListLineGroups: [
                            {
                                groupName: uncategorizedGroupName.toLowerCase(),
                                originalGroupName: "",
                                notes: "",
                                qtyOrdered: o.qtyOrdered,
                                sortOrder: 0,
                            },
                        ],
                    }),
                },
            };

            newProductInfo.push(newInfo);
        });

        const wishListGroups = [uncategorizedGroupName.toLowerCase()];
        
        addToWishList({
            productInfos: newProductInfo,
            selectedWishList: isNewList ? undefined : wishListsDataView.value?.find(o => o.id === selectedWishListId),
            newListName,
            groups: wishListGroups,

            onSuccess: async (wishList: WishListModel) => {
                setCookie(LastUpdatedListIdCookieName, wishList.id);
                setSelectedWishListId(wishList.id);
                toasterContext.addToast({
                    body: siteMessage("Lists_ProductAdded"),
                    messageType: "success",
                    isFocusAfterMount: true,
                });
                if (isScheduled) {
                    const updatedWishList = await getWishList({ wishListId: wishList.id, expand: ["schedule"] });
                    setCurrentWishList(updatedWishList);
                    setScheduleReminderModalIsOpen(true);
                    setInProgress(false);
                } else {
                    modalCloseHandler();
                }
            },
            onError: errorMessage => {
                setInProgress(false);

                if (isNewList) {
                    setNewListNameError(errorMessage);
                } else {
                    modalCloseHandler();
                    toasterContext.addToast({ body: errorMessage, messageType: "danger" });
                }
            },
            onComplete(wishListProps: any) {
                setInProgress(false);
                if (wishListProps?.result?.wishList) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.(wishListProps?.result?.wishList);
                } else if (wishListProps?.result?.errorMessage) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onError?.(wishListProps?.result?.errorMessage);
                }
            },
        } as AddToWishListParameter);
    };

    const handleRedirect = () => {
        modalCloseHandler();
        closeQuickViewModal({
            onComplete: () => {
                history.push(projectListsPageUrl!);
            },
        });
    };

    const title =
        currentStep === ""
            ? siteMessage("Custom_AddToList_CreateNewList")
            : siteMessage("Custom_AddToList_AddToList");

    const returnUrl = location.pathname + location.search;

    return (
        <Modal
            className="add-top-padding"
            size={600}
            headline={isAuthenticated ? title : translate("Please Sign In")}
            isOpen={modalIsOpen}
            handleClose={modalCloseHandler}
            {...styles.modal}
        >
            <StyledWrapper {...styles.innerWrapper} data-test-selector="productAddToListModal">
                {!isAuthenticated && (
                    <Typography data-test-selector="productAddToListModal_requireSignIn" {...styles.signInMessageText}>
                        {siteMessage(
                            "Lists_Must_Sign_In_Spire",
                            `${signInUrl}?returnUrl=${encodeURIComponent(returnUrl)}&clientRedirect=true`,
                        )}
                    </Typography>
                )}
                {isAuthenticated && (
                    <LoadingOverlay loading={inProgress}>
                        <StyledWrapper {...styles.copyWrapper}>
                            <Typography {...styles.descriptionText}>
                                {siteMessage("Custom_AddToList_HeadCopy")}
                            </Typography>
                        </StyledWrapper>
                        <StyledWrapper
                            css={css`
                                position: relative;
                            `}
                        >
                            {/**  ADD TO EXISTING */}
                            <StyledWrapper>
                                <DynamicDropdown
                                    label={translate("Select Project List")}
                                    {...styles.listsSelect}
                                    isLoading={wishListsDataView.isLoading}
                                    onInputChange={listsInputChangeHandler}
                                    onSelectionChange={listChangeHandler}
                                    selected={selectedWishListId}
                                    options={options}
                                    error={selectListError}
                                    data-test-selector="productAddToListModal_listSelect"
                                />
                                {selectedWishListId === "new" && (
                                    <TextField
                                        label={translate("Project List Name")}
                                        value={newListName}
                                        error={newListNameError}
                                        maxLength={50}
                                        onChange={newListInputChangeHandler}
                                        placeholder={translate("New Project List Name")}
                                        {...styles.newListInput}
                                        data-test-selector="productAddToListModal_newName"
                                    />
                                )}
                                <Checkbox
                                    {...styles.scheduleReminderCheckbox}
                                    checked={isScheduled}
                                    onChange={scheduleReminderChangeHandler}
                                >
                                    {siteMessage("Lists_Schedule_Reminders")}
                                </Checkbox>
                                <StyledWrapper {...styles.buttonsWrapper} className="modal-actions">
                                    <Button
                                        {...styles.addButton}
                                        onClick={e => addToListButtonClickHandler(e)}
                                        data-test-selector="productAddToListModal_addButton"
                                        disabled={inProgress}
                                    >
                                        {translate("Add To Project List")}
                                    </Button>
                                    <Button {...styles.cancelButton} onClick={modalCloseHandler}>
                                        {translate("Cancel")}
                                    </Button>
                                </StyledWrapper>
                                {isScheduled && currentWishList && (
                                    <ScheduleReminderModal
                                        wishList={currentWishList}
                                        isOpen={scheduleReminderModalIsOpen}
                                        handleClose={closeScheduleReminderModalHandler}
                                        extendedStyles={styles.scheduleReminderModal}
                                    />
                                )}
                            </StyledWrapper>
                        </StyledWrapper>
                    </LoadingOverlay>
                )}
            </StyledWrapper>
        </Modal>
    );
};

export type GroupProps = {
    imagePath: string;
    groupName: string;
};

export const GroupItem = ({ imagePath, groupName }: GroupProps) => {
    return (
        <StyledWrapper className="item-wrapper">
            <LazyImage
                src={imagePath}
                width="144px"
                height="160px"
                cloudinaryWidth="144"
                cloudinary={true}
                css={css`
                    margin-bottom: 15px;
                `}
            />
            <Typography lineHeight="16px" transform="uppercase" color="secondary">
                {groupName}
            </Typography>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(AddToListModal));
